/**
 * Числа й гроші в інтерфейсі.
 *
 * Винесено сюди, бо копій `Intl.NumberFormat('uk-UA')` у проєкті вже чотири
 * (залишки, рецептура, рух, позиції документа), і комерційні пропозиції додали
 * б п'яту — вже з іншим правилом (два знаки й валюта). Прецедент той самий, що
 * з `formatPhone`: щойно формат став неоднаковим у різних місцях, він має жити
 * в одному.
 */

/** Кількість: без зайвих нулів, з розділювачем тисяч. */
export const formatQuantity = (value: number | null | undefined): string => {
    if (value === null || value === undefined || Number.isNaN(value)) return '—'

    return new Intl.NumberFormat('uk-UA', { maximumFractionDigits: 3 }).format(value)
}

/**
 * Сума: завжди два знаки — так стовпчик читається як гроші, а не як набір
 * різнорозрядних чисел.
 */
export const formatMoney = (value: number | null | undefined): string => {
    if (value === null || value === undefined || Number.isNaN(value)) return '—'

    return new Intl.NumberFormat('uk-UA', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(value)
}

/** Сума з валютою — для підсумків і карток, де валюта не очевидна з колонки. */
export const formatMoneyWithCurrency = (
    value: number | null | undefined,
    currency = 'EUR'
): string => (value === null || value === undefined ? '—' : `${formatMoney(value)} ${currency}`)

/**
 * Гроші без копійок — для плашок і підказок графіків.
 *
 * Копійки там лише додають шуму: на підсумку в сто мільйонів два знаки після
 * коми не змінюють рішення, а рядок робить удвічі довшим.
 */
export const formatRoundMoney = (value: number, currency: string): string =>
    `${new Intl.NumberFormat('uk-UA', { maximumFractionDigits: 0 }).format(value)} ${currency}`

/**
 * Скорочене число для поділки осі.
 *
 * ⚠️ Осі графіків мають фіксовану ширину: без скорочення «12 000 000» мовчки
 * обрізається до «2 000 000», і графік бреше на порядок.
 */
export const formatCompact = (value: number): string => {
    if (Math.abs(value) >= 1_000_000) return `${Math.round(value / 100_000) / 10} млн`
    if (Math.abs(value) >= 1_000) return `${Math.round(value / 1_000)} тис`

    return String(value)
}
